import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { states } from '@/constants/lifecycle';

export const fitToPage = (items, pageNumber, pageSize) => {
  const attributes = { items, pageNumber };
  if (items.length > pageSize) {
    attributes.items = items.slice(0, pageSize);
    attributes.pageNumber = 1;
  }

  return attributes;
};

export const goToPage = (fetch, { append, page, pageSize }, { current }) => {
  fetch({ append, page, pageSize });
  current.scrollIntoView();
};

export const goToCategoryPage = (fetch, category, page, { current }) => {
  fetch(category, page, null, false);
  current.scrollIntoView();
};

export const goToCodePage = (fetch, category, code, page, { current }) => {
  fetch(category, code, page, null, false);
  current.scrollIntoView();
};

export const pagingAttributes = (pageSize = null) => ({ append: false, page: 1, pageSize });

export const pagingConfig = (pageSize = null) => ({ attributes: pagingAttributes(pageSize) });

export const pagingHookConfig = (dependencies = [], reFetch = () => false, valid = () => true) => ({
  ...pagingConfig(),
  dependencies,
  reFetch,
  valid,
});

export const reFetchConfig = ({ length }, { pageSize, total }, dependencies = []) => (
  pagingHookConfig(dependencies, () => length < (pageSize && total))
);

const isValid = (status, config) => (status === states.NONE && config.valid()) || config.reFetch();

const pageChecks = (current, count) => {
  const page = current + 1;
  return { page, valid: page <= count };
};

export const onScroll = (fetch, { currentPage, pageCount, pageSize }) => {
  const { page, valid } = pageChecks(currentPage, pageCount);
  if (valid) { fetch({ append: true, page, pageSize }); }
};

export const onCategoryScroll = (fetch, category, current, count, pageSize) => {
  const { page, valid } = pageChecks(current, count);
  if (valid) { fetch(category, page, pageSize, true); }
};

export const onCodeScroll = (fetch, category, code, current, count, pageSize) => {
  const { page, valid } = pageChecks(current, count);
  if (valid) { fetch(category, code, page, pageSize, true); }
};

const useFetchByCategory = (
  fetch,
  category,
  status,
  config = pagingHookConfig(),
) => {
  useEffect(() => {
    if (isValid(status, config)) {
      const { page, pageSize, append } = config.attributes;
      fetch(category, page, pageSize, append);
    }
  }, config.dependencies);
};

const useFetchByCode = (
  fetch,
  category,
  code,
  status,
  config = pagingHookConfig(),
) => {
  useEffect(() => {
    if (isValid(status, config)) {
      const { page, pageSize, append } = config.attributes;
      fetch(category, code, page, pageSize, append);
    }
  }, config.dependencies);
};

const useFetch = (type, status, config = pagingHookConfig()) => {
  const handler = useDispatch();

  useEffect(() => {
    if (isValid(status, config)) {
      handler({ type, ...config.attributes });
    }
  }, config.dependencies);
};

const useDispatchByCategory = (
  type,
  category,
  status,
  config = pagingHookConfig(),
) => {
  const handler = useDispatch();

  useEffect(() => {
    if (isValid(status, config)) {
      handler({ type, category, ...config.attributes });
    }
  }, config.dependencies);
};

const useDispatchByCode = (
  type,
  category,
  code,
  status,
  config = pagingHookConfig(),
) => {
  const handler = useDispatch();

  useEffect(() => {
    if (isValid(status, config)) {
      handler({
        type, category, code, ...config.attributes,
      });
    }
  }, config.dependencies);
};

export default {
  useFetch,
  useFetchByCode,
  useFetchByCategory,
  useDispatchByCategory,
  useDispatchByCode,
};
